import { useState, useEffect } from 'react';
import { Profile } from '../../types';
import { supabase } from '../../lib/supabase';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { AddEmployeeForm } from '../../components/AddEmployeeForm';
import { RoleChangePanel } from '../../components/RoleChangePanel';

export const AdminEmployees = () => {
  const [employees, setEmployees] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRoleChange, setShowRoleChange] = useState(false);

  const loadEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase.from('profiles').select('*').eq('role', 'EMPLOYEE').order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setEmployees((data ?? []) as Profile[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const toggleStatus = async (id: string, status: 'ACTIVE' | 'INACTIVE') => {
    await supabase.from('profiles').update({ status }).eq('id', id);
    await loadEmployees();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
        <p className="text-sm text-gray-600">Manage employee accounts and role conversions.</p>
      </div>
      <AddEmployeeForm onAdded={loadEmployees} />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setShowRoleChange(false)}
          className={`rounded px-4 py-2 text-sm font-medium ${!showRoleChange ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          Employees
        </button>
        <button
          type="button"
          onClick={() => setShowRoleChange(true)}
          className={`rounded px-4 py-2 text-sm font-medium ${showRoleChange ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
        >
          User ⇄ Employee
        </button>
      </div>
      {showRoleChange ? (
        <RoleChangePanel />
      ) : loading ? (
        <Loading full />
      ) : error ? (
        <ErrorAlert message={error} onRetry={loadEmployees} />
      ) : employees.length === 0 ? (
        <Empty title="No employees" message="Create employee accounts to see them here." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Department</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {employees.map((employee: Profile) => (
                <tr key={employee.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{employee.full_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{employee.email ?? employee.phone ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{employee.department ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${employee.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {employee.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => toggleStatus(employee.id, employee.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                      className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                    >
                      {employee.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
